/**
 * Spec 200 — 2nd-pass UX redesign R1: master-detail shell.
 *
 * Promotes the paired surfaces (WorkList, WorkDetail, NewWork,
 * Settings) from a single-pane scroll to a Linear/Slack/Notion-style
 * sidebar+main layout on desktop (≥1280 px). Below the breakpoint
 * the sidebar disappears and each route falls back to its existing
 * top header — mobile/tablet stay untouched.
 *
 * The sidebar surfaces:
 *   - Brand block (Cuadrilla web)
 *   - Identity chip (name + role)
 *   - Vertical nav: Trabajos / Nuevo / Ajustes (NavLink active state)
 *   - Footer hint: ⌘K / Ctrl+K opens the command palette
 *
 * B8 — the sidebar can collapse to a 64 px rail (icon-only, like
 * VSCode / Linear). The expanded/collapsed preference persists in
 * localStorage so it survives reloads. Hover tooltips (native
 * `title`) restore the labels when collapsed.
 *
 * The route headers (.wl-header, .wd-header, .settings-header) hide
 * themselves with a `.app-paired-shell--has-sidebar` body-level class
 * that the shell adds when the sidebar is rendered. This keeps the
 * route-level CSS local — no global header rule that mobile would
 * also pick up.
 *
 * Why a wrapper, not a layout route: the existing routes own their
 * own scroll + sticky behaviour, and the FAB is positioned fixed.
 * Wrapping them in a CSS-grid shell preserves all of that without
 * forcing a refactor of every route.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Briefcase,
  Megaphone,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings as SettingsIcon,
  Users,
} from 'lucide-react';

import { BrandMark } from './BrandMark';
import { Tooltip, TooltipProvider } from './Tooltip';
import { Avatar } from './ui/Avatar';
import type { WebIdentity } from '../store/keys';

import './AppShell.css';

interface AppShellProps {
  identity: WebIdentity;
  children: React.ReactNode;
}

/* ── Topbar context ────────────────────────────────────────────────
 * Routes inject right-aligned actions into the desktop topbar via
 * `useTopbar().setTopbarActions(<button ... />)` inside a useEffect.
 * The slot is also used to publish the current detail title (work
 * title / client name) so the breadcrumb can render it. Everything
 * resets to `null` when the route unmounts.
 */
interface TopbarContextValue {
  setTopbarActions: (node: ReactNode) => void;
  setTopbarDetailTitle: (title: string | null) => void;
}

const TopbarContext = createContext<TopbarContextValue | null>(null);

export function useTopbar(): TopbarContextValue {
  const ctx = useContext(TopbarContext);
  if (!ctx) {
    // Outside <AppShell> (e.g. /pair) the calls become no-ops so
    // routes don't have to guard every effect.
    return {
      setTopbarActions: () => {},
      setTopbarDetailTitle: () => {},
    };
  }
  return ctx;
}

/* Route → page title mapping. Detail routes (/works/:id, /clients/:slug)
 * fall through to the breadcrumb path below. */
const ROUTE_TITLES: Record<string, string> = {
  '/': 'Trabajos',
  '/stats': 'Estadísticas',
  '/clients': 'Clientes',
  '/broadcasts': 'Avisos',
  '/settings': 'Ajustes',
  '/settings/perfil': 'Ajustes',
  '/settings/equipo': 'Ajustes',
  '/settings/apariencia': 'Ajustes',
  '/settings/identidad': 'Ajustes',
  '/settings/privacidad': 'Ajustes',
  '/settings/sesiones-web': 'Ajustes',
  '/settings/cuenta': 'Ajustes',
  '/works/new': 'Nuevo trabajo',
};

const ROLE_LABEL: Record<WebIdentity['role'], string> = {
  creator: 'Creador',
  admin: 'Administrador',
  member: 'Miembro',
};

const COLLAPSED_KEY = 'cuadrilla-web.sidebar-collapsed-v1';

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppShell({ identity, children }: AppShellProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsedPreference);
  const [topbarActions, setTopbarActions] = useState<ReactNode>(null);
  const [topbarDetailTitle, setTopbarDetailTitle] = useState<string | null>(null);

  const topbarValue = useMemo<TopbarContextValue>(
    () => ({
      setTopbarActions: (node: ReactNode) => setTopbarActions(node),
      setTopbarDetailTitle: (title: string | null) => setTopbarDetailTitle(title),
    }),
    [],
  );

  // Reset topbar slots when the route changes so a stale title /
  // action set from the previous route never bleeds through.
  useEffect(() => {
    setTopbarActions(null);
    setTopbarDetailTitle(null);
  }, [location.pathname]);

  // Resolve the topbar title + breadcrumb shape from the current path.
  const topbarMeta = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/works/') && path !== '/works/new') {
      return {
        kind: 'breadcrumb' as const,
        parentTo: '/',
        parentLabel: 'Trabajos',
        currentLabel: topbarDetailTitle ?? 'Trabajo',
      };
    }
    if (path.startsWith('/clients/')) {
      return {
        kind: 'breadcrumb' as const,
        parentTo: '/clients',
        parentLabel: 'Clientes',
        currentLabel: topbarDetailTitle ?? 'Cliente',
      };
    }
    return {
      kind: 'title' as const,
      label: ROUTE_TITLES[path] ?? '',
    };
  }, [location.pathname, topbarDetailTitle]);

  // Tag <body> with a class so route-level CSS can collapse its
  // own header at the desktop breakpoint without inventing a
  // global media query. The class is removed on unmount so an
  // identity-loss → /pair fallback gets a clean body.
  useEffect(() => {
    document.body.classList.add('has-app-shell');
    return () => document.body.classList.remove('has-app-shell');
  }, []);

  // Mirror the collapsed flag on <body> so any descendant CSS that
  // needs to react (e.g. main-area padding) can do so without prop
  // drilling.
  useEffect(() => {
    document.body.classList.toggle('has-app-shell--collapsed', collapsed);
    return () => document.body.classList.remove('has-app-shell--collapsed');
  }, [collapsed]);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Private mode — silent.
      }
      return next;
    });
  }

  return (
    <TooltipProvider>
    <TopbarContext.Provider value={topbarValue}>
    <div className={`app-paired-shell${collapsed ? ' app-paired-shell--collapsed' : ''}`}>
      {/* A5 a11y — keyboard users can jump past the sidebar nav
          with a single Tab + Enter. Visible only when focused. */}
      <a className="skip-link" href="#main-content">
        Saltar al contenido principal
      </a>
      <aside
        className={`app-sidebar${collapsed ? ' app-sidebar--collapsed' : ''}`}
        aria-label="Navegación principal"
        data-testid="app-sidebar"
      >
        <div className="app-sidebar-brand">
          <BrandMark size={28} ariaLabel="Cuadrilla" />
          <span className="app-sidebar-brand-text">Cuadrilla</span>
          <span className="app-sidebar-brand-suffix">web</span>
        </div>

        <Tooltip label={t("sidebar.newWork")} side="right" disabled={!collapsed}>
          <NavLink
            to="/works/new"
            aria-label={t("sidebar.newWork")}
            className="app-sidebar-cta"
            data-testid="app-sidebar-link-new"
          >
            <Plus size={20} strokeWidth={2} aria-hidden="true" />
            <span className="app-sidebar-link-label">{t("sidebar.newWork")}</span>
          </NavLink>
        </Tooltip>

        <nav className="app-sidebar-nav">
          <Tooltip label={t("sidebar.works")} side="right" disabled={!collapsed}>
            <NavLink
              to="/"
              end
              aria-label={t("sidebar.works")}
              /* String className (not function) — Radix Tooltip's
                 `asChild` Slot stringifies a function-form className,
                 so the rule selector never matches in collapsed mode.
                 We compute active state from the location ourselves. */
              className={`app-sidebar-link ${
                location.pathname === '/' || location.pathname.startsWith('/works/')
                  ? 'app-sidebar-link--active'
                  : ''
              }`}
              data-testid="app-sidebar-link-works"
            >
              <Briefcase size={20} strokeWidth={2} aria-hidden="true" />
              <span className="app-sidebar-link-label">{t("sidebar.works")}</span>
            </NavLink>
          </Tooltip>
          {/* Spec 217 PR1 — Statistics dashboard between Trabajos and
              Ajustes. Order matches the audit doc's "flat sidebar"
              decision (tasks/investigations/217_audit_2026-04-26.md §5). */}
          <Tooltip label={t("sidebar.stats")} side="right" disabled={!collapsed}>
            <NavLink
              to="/stats"
              aria-label={t("sidebar.stats")}
              className={`app-sidebar-link ${
                location.pathname === '/stats' ? 'app-sidebar-link--active' : ''
              }`}
              data-testid="app-sidebar-link-stats"
            >
              <BarChart3 size={20} strokeWidth={2} aria-hidden="true" />
              <span className="app-sidebar-link-label">{t("sidebar.stats")}</span>
            </NavLink>
          </Tooltip>
          {/* Spec 217 PR4.b — /clients role gate. Same pattern as
              /broadcasts below: hide the link for members so they
              don't bounce off a fallback page. */}
          {(identity.role === 'creator' || identity.role === 'admin') && (
            <Tooltip label={t("sidebar.clients")} side="right" disabled={!collapsed}>
              <NavLink
                to="/clients"
                aria-label={t("sidebar.clients")}
                className={`app-sidebar-link ${
                  location.pathname === '/clients' || location.pathname.startsWith('/clients/')
                    ? 'app-sidebar-link--active'
                    : ''
                }`}
                data-testid="app-sidebar-link-clients"
              >
                <Users size={20} strokeWidth={2} aria-hidden="true" />
                <span className="app-sidebar-link-label">{t("sidebar.clients")}</span>
              </NavLink>
            </Tooltip>
          )}
          {/* Spec 217 PR4.b — /broadcasts is admin/creator only at the
              route level (RoleGuard in App.tsx). The sidebar link only
              renders for those roles so a member doesn't see a dead
              entry that takes them to a fallback. */}
          {(identity.role === 'creator' || identity.role === 'admin') && (
            <Tooltip label={t("sidebar.broadcasts")} side="right" disabled={!collapsed}>
              <NavLink
                to="/broadcasts"
                aria-label={t("sidebar.broadcasts")}
                className={`app-sidebar-link ${
                  location.pathname === '/broadcasts' ? 'app-sidebar-link--active' : ''
                }`}
                data-testid="app-sidebar-link-broadcasts"
              >
                <Megaphone size={20} strokeWidth={2} aria-hidden="true" />
                <span className="app-sidebar-link-label">{t("sidebar.broadcasts")}</span>
              </NavLink>
            </Tooltip>
          )}
          <Tooltip label={t("sidebar.settings")} side="right" disabled={!collapsed}>
            <NavLink
              to="/settings"
              aria-label={t("sidebar.settings")}
              className={`app-sidebar-link ${
                location.pathname === '/settings' ? 'app-sidebar-link--active' : ''
              }`}
              data-testid="app-sidebar-link-settings"
            >
              <SettingsIcon size={20} strokeWidth={2} aria-hidden="true" />
              <span className="app-sidebar-link-label">{t("sidebar.settings")}</span>
            </NavLink>
          </Tooltip>
        </nav>

        <div className="app-sidebar-footer">
          {/* Show both shortcuts so cross-platform users (Mac + Win/Linux)
              learn the right chord without having to guess. */}
          <span className="app-sidebar-footer-hint" aria-hidden="true">
            <kbd>Ctrl</kbd>
            <kbd>K</kbd>
            <span className="app-sidebar-footer-hint-sep">·</span>
            <kbd>⌘</kbd>
            <kbd>K</kbd>
            <span>buscar</span>
          </span>
          <Tooltip label={collapsed ? 'Expandir' : 'Contraer'} side="right">
            <button
              type="button"
              className="app-sidebar-toggle"
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expandir barra lateral' : 'Contraer barra lateral'}
              aria-expanded={!collapsed}
              data-testid="app-sidebar-toggle"
            >
              {collapsed ? (
                <PanelLeftOpen size={20} strokeWidth={2} aria-hidden="true" />
              ) : (
                <PanelLeftClose size={20} strokeWidth={2} aria-hidden="true" />
              )}
            </button>
          </Tooltip>
        </div>
      </aside>

      <div className="app-main-area">
        <header className="app-topbar" data-testid="app-topbar">
          {topbarMeta.kind === 'breadcrumb' ? (
            <nav className="app-topbar-breadcrumb" aria-label="Breadcrumb">
              <Link to={topbarMeta.parentTo} className="app-topbar-breadcrumb-parent">
                {topbarMeta.parentLabel}
              </Link>
              <span className="app-topbar-breadcrumb-sep" aria-hidden="true">
                /
              </span>
              <h1 className="app-topbar-title app-topbar-breadcrumb-current">
                {topbarMeta.currentLabel}
              </h1>
            </nav>
          ) : (
            <h1 className="app-topbar-title">{topbarMeta.label}</h1>
          )}
          <div className="app-topbar-actions" data-testid="app-topbar-actions">
            {topbarActions}
          </div>
          <Link
            to="/settings"
            className="app-topbar-identity"
            data-testid="app-topbar-identity"
            aria-label="Abrir mis ajustes"
          >
            <Avatar name={identity.name} size="sm" ariaHidden />
            <div className="app-topbar-identity-text">
              <span className="app-topbar-identity-name">{identity.name}</span>
              <span className="app-topbar-identity-role">{ROLE_LABEL[identity.role]}</span>
            </div>
          </Link>
        </header>
        {children}
      </div>
    </div>
    </TopbarContext.Provider>
    </TooltipProvider>
  );
}
